import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PickupsStatsTab } from "./tabs/PickupsStatsTab";
import { VehiclesStatsTab } from "./tabs/VehiclesStatsTab";
import { ProductsStatsTab } from "./tabs/ProductsStatsTab";
import { EconomyStatsTab } from "./tabs/EconomyStatsTab";
import { EventsStatsTab } from "./tabs/EventsStatsTab";
import { GeneralExpenseStatsTab } from "./tabs/GeneralExpenseStatsTab";
import { getMonthRange, getTodayRange, getWeekRange, getYearRange, Preset, DateRange } from "./utils/date";
import {ProvidersStatsTab} from "./tabs/ProvidersStatsTab";


// --- Inline SVG icons ---
const CalendarIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
    </svg>
);
const RefreshIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v7h-7"/>
    </svg>
);


const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    visible: (i = 1) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06 } }),
};


type Tab = "recogidas" | "vehiculos" | "productos" | "proveedores" | "economia" | "eventos" | "gastos";

export default function DashboardView() {
// Tabs
    const [tab, setTab] = useState<Tab>("recogidas");


// Filtros de fecha compartidos
    const [preset, setPreset] = useState<Preset>("month");
    const [range, setRange] = useState<DateRange>(getMonthRange());
    const [customStart, setCustomStart] = useState<string>(range.start);
    const [customEnd, setCustomEnd] = useState<string>(range.end);


// Trigger para forzar recargas desde el botón "Actualizar"
    const [refreshKey, setRefreshKey] = useState(0);


// INIT: mes actual
    useEffect(() => {
        const r = getMonthRange();
        setPreset("month");
        setRange(r);
        setCustomStart(r.start);
        setCustomEnd(r.end);
    }, []);

    const applyRangeAndFetch = (r: DateRange, newPreset?: Preset) => {
        if (newPreset) setPreset(newPreset);
        setRange(r);  
    };


    const applyPreset = (p: Preset) => {
        switch (p) {
            case "today": return applyRangeAndFetch(getTodayRange(), "today");
            case "week": return applyRangeAndFetch(getWeekRange(), "week");
            case "month": return applyRangeAndFetch(getMonthRange(), "month");
            case "year": return applyRangeAndFetch(getYearRange(), "year");
            case "custom":return applyRangeAndFetch({ start: customStart, end: customEnd }, "custom");
        }
    };

    const handleTabChange = (next: Tab) => {
        setTab(next);
        const monthRange = getMonthRange();
        setPreset("month");
        setRange(monthRange);
        setCustomStart(monthRange.start);
        setCustomEnd(monthRange.end);
    };


    const handleRefresh = () => setRefreshKey((k) => k + 1);

    return (
        <div className="p-6 lg:p-10 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="text-2xl lg:text-3xl font-semibold tracking-tight">
                    Panel de control
                </motion.h1>
                <div className="flex gap-2">
                    <button onClick={handleRefresh} className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-gray-50">
                        <RefreshIcon className="h-4 w-4" /> Actualizar
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="inline-flex rounded-xl border bg-white p-1">
                <button
                    onClick={() => handleTabChange("recogidas")}
                    className={`px-3 py-1.5 text-sm rounded-lg ${tab === "recogidas" ? "bg-gray-900 text-white" : "text-gray-700"}`}
                >
                    Recogidas
                </button>
                <button
                    onClick={() => handleTabChange("vehiculos")}
                    className={`px-3 py-1.5 text-sm rounded-lg ${tab === "vehiculos" ? "bg-gray-900 text-white" : "text-gray-700"}`}
                >
                    Consumo y vehículos
                </button>
                <button
                    onClick={() => handleTabChange("productos")}
                    className={`px-3 py-1.5 text-sm rounded-lg ${tab === "productos" ? "bg-gray-900 text-white" : "text-gray-700"}`}
                >
                    Productos
                </button>
                <button
                    onClick={() => handleTabChange("proveedores")}
                    className={`px-3 py-1.5 text-sm rounded-lg ${tab === "proveedores" ? "bg-gray-900 text-white" : "text-gray-700"}`}
                >
                    Proveedores
                </button>
                {/*<button*/}
                {/*    onClick={() => handleTabChange("economia")}*/}
                {/*    className={`px-3 py-1.5 text-sm rounded-lg ${tab === "economia" ? "bg-gray-900 text-white" : "text-gray-700"}`}*/}
                {/*>*/}
                {/*    Información económica*/}
                {/*</button>*/}
                {/*<button*/}
                {/*    onClick={() => handleTabChange("eventos")}*/}
                {/*    className={`px-3 py-1.5 text-sm rounded-lg ${tab === "eventos" ? "bg-gray-900 text-white" : "text-gray-700"}`}*/}
                {/*>*/}
                {/*    Alertas y eventos*/}
                {/*</button>*/}
                <button
                    onClick={() => handleTabChange("gastos")}
                    className={`px-3 py-1.5 text-sm rounded-lg ${tab === "gastos" ? "bg-gray-900 text-white" : "text-gray-700"}`}
                >
                    Gastos
                </button>
            </div>

            {/* Filtros */}
            <motion.div variants={fadeUp} initial="hidden" animate="visible" className="grid grid-cols-1 gap-3">
                { tab !== "proveedores" && (
                    <div className="col-span-1">
                        <label className="text-sm font-medium">Rango de fechas</label>
                        <div className="mt-1 grid grid-cols-6 gap-2">
                            <button onClick={() => applyPreset("today")} className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 ${preset === "today" ? "bg-gray-900 text-white" : ""}`}>Hoy</button>
                            <button onClick={() => applyPreset("week")} className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 ${preset === "week" ? "bg-gray-900 text-white" : ""}`}>Esta semana</button>
                            <button onClick={() => applyPreset("month")} className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 ${preset === "month" ? "bg-gray-900 text-white" : ""}`}>Este mes</button>
                            <button onClick={() => applyPreset("year")} className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 ${preset === "year" ? "bg-gray-900 text-white" : ""}`}>Este año</button>
                            <button onClick={() => setPreset("custom")} className={`inline-flex items-center justify-center rounded-xl border px-3 py-2 text-sm hover:bg-gray-50 ${preset === "custom" ? "bg-gray-900 text-white" : ""}`}>Personalizado</button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                            <CalendarIcon className="h-3 w-3" /> {range.start} → {range.end}
                        </p>
                    </div>
                )}
                {preset === "custom" && (
                    <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-5 gap-3">
                        <div className="col-span-2">
                            <label className="text-sm font-medium">Desde</label>
                            <input type="date" className="mt-1 w-full rounded-xl border px-3 py-2 text-sm bg-white" value={customStart} max={customEnd} onChange={(e) => setCustomStart(e.target.value)} />
                        </div>
                        <div className="col-span-2">
                            <label className="text-sm font-medium">Hasta</label>
                            <input type="date" className="mt-1 w-full rounded-xl border px-3 py-2 text-sm bg-white" value={customEnd} min={customStart} onChange={(e) => setCustomEnd(e.target.value)} />
                        </div>
                        <div className="col-span-1 flex items-end">
                            <button onClick={() => applyPreset("custom")} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 text-white px-3 py-2 text-sm hover:opacity-90">
                                <CalendarIcon className="h-4 w-4" /> Aplicar
                            </button>
                        </div>
                    </div>
                )}
            </motion.div>

            {/* Contenido por pestaña */}

            {/* Pestaña Recogidas */}
            {tab === "recogidas" && (
                <PickupsStatsTab range={range} refreshKey={refreshKey} />
            )}

            {/* Pestaña Consumo y vehículos */}
            {tab === "vehiculos" && (
                <VehiclesStatsTab range={range} refreshKey={refreshKey} />
            )}

            {/* Pestaña Productos */}
            {tab === "productos" && (
                <ProductsStatsTab range={range} refreshKey={refreshKey} />
            )}

            {/* Pestaña Proveedores */}
            {tab === "proveedores" && (
                <ProvidersStatsTab range={range} refreshKey={refreshKey} />
            )}

            {/* Pestaña Gastos */}
            {tab === "gastos" && (
                <GeneralExpenseStatsTab range={range} refreshKey={refreshKey} />
            )}

            {/*/!* Pestaña Información económica *!/*/}
            {/*{tab === "economia" && (*/}
            {/*    <EconomyStatsTab range={range} refreshKey={refreshKey} />*/}
            {/*)}*/}

            {/*/!* Pestaña Alertas y eventos *!/*/}
            {/*{tab === "eventos" && (*/}
            {/*    <EventsStatsTab range={range} refreshKey={refreshKey} />*/}
            {/*)}*/}
        </div>
    );
}
